import { EnvelopeIcon, GlobeAltIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { LatLng } from "@sp-crm/core";
import { HoveredMapEntity } from "components/community-search/community-map/types";
import { Icon } from "components/icon";
import { ClampedText } from "components/ui/clamped-text";
import { defaultLinkStyle } from "components/ui/link";
import { SearchUserProfilesQuery } from "generated/graphql";
import React from "react";
import { PhoneSingleNumber } from "util/phone-display";
import { breakNewlines } from "util/text";
import { sanitizeUri } from "util/uri";
import { AgentDirectoryThumbnail } from "./agent-directory-thumbnail";

interface AgentDirectorySearchCardProps {
    agent: SearchUserProfilesQuery["searchUserProfiles"]["hits"][number];
    onHover: (agent: HoveredMapEntity) => void;
    onEndHover: () => void;
    detailsMode?: "expanded" | "collapsed";
    children?: React.ReactNode;
}

export const AgentDirectorySearchCard: React.FC<
    AgentDirectorySearchCardProps
> = props => {
    const { agent, onHover, onEndHover } = props;
    const detailsMode = props.detailsMode || "expanded";

    const [expanded, setExpanded] = React.useState(false);

    const handleMouseEnter = () => {
        let location: LatLng | null = null;
        if (agent.latitude && agent.longitude) {
            location = {
                lat: parseFloat(agent.latitude),
                lng: parseFloat(agent.longitude),
            };
        }
        onHover({
            entityId: agent.id,
            location,
        });
    };

    const handleMouseLeave = () => {
        onEndHover();
    };

    return (
        <div
            className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <div className="flex space-x-6 w-full items-start justify-between p-6">
                <div>
                    <AgentDirectoryThumbnail agent={agent} />
                </div>
                <div className="flex-1 overflow-auto">
                    <div className="flex space-x-2">
                        <div className="flex-1">
                            <h3 className="twoverride text-gray-900 text-lg">
                                <div className="flex space-x-2 items-center">
                                    <div className="w-6">
                                        <Icon name="40_ReferralPerson" />
                                    </div>
                                    <div>{agent.name || "(no name)"}</div>
                                </div>
                            </h3>
                            <div className="text-sm text-gray-500">
                                {agent.businessName ? (
                                    <div>{agent.businessName}</div>
                                ) : null}
                                {agent.territories?.length > 0 ? (
                                    <ul>
                                        {agent.territories.map((territory, i) => (
                                            <li key={i}>
                                                {territory.city}, {territory.state}{" "}
                                                {territory.zip}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                                {agent.website ? (
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={sanitizeUri(agent.website)}
                                        className={`flex items-center space-x-1 ${defaultLinkStyle}`}>
                                        <GlobeAltIcon className="text-gray-500 w-4 h-4" />
                                        <div>{sanitizeUri(agent.website)}</div>
                                    </a>
                                ) : null}
                                {agent.phone ? (
                                    <div className="space-x-1 flex items-center">
                                        <PhoneIcon className="text-gray-500 w-4 h-4" />
                                        <PhoneSingleNumber phoneNumber={agent.phone} />
                                    </div>
                                ) : null}
                                {agent.email ? (
                                    <a
                                        href={`mailto:${agent.email}`}
                                        className={`flex items-center space-x-1 ${defaultLinkStyle}`}>
                                        <EnvelopeIcon className="text-gray-500 w-4 h-4" />
                                        <div>{agent.email}</div>
                                    </a>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {detailsMode === "expanded" || expanded ? (
                        <div className="mt-4 border-t border-gray-200 pt-2">
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                {agent.summary && (
                                    <div className="sm:col-span-2">
                                        <dt className="text-sm font-medium text-gray-500">
                                            Summary
                                        </dt>
                                        <dd className="mt-1 text-sm text-gray-900">
                                            <ClampedText lines={3}>
                                                {breakNewlines(agent.summary)}
                                            </ClampedText>
                                        </dd>
                                    </div>
                                )}
                                {agent.approach && (
                                    <div className="sm:col-span-2">
                                        <dt className="text-sm font-medium text-gray-500">
                                            Operational Approach
                                        </dt>
                                        <dd className="mt-1 text-sm text-gray-900">
                                            <ClampedText lines={3}>
                                                {breakNewlines(agent.approach)}
                                            </ClampedText>
                                        </dd>
                                    </div>
                                )}
                                {agent.certifications?.length > 0 && (
                                    <div className="sm:col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">
                                            Certifications
                                        </dt>
                                        <dd className="mt-1 text-sm text-gray-900">
                                            <ul>
                                                {agent.certifications.map((cert, i) => (
                                                    <li key={i}>{cert}</li>
                                                ))}
                                            </ul>
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </div>
                    ) : null}
                    {detailsMode === "collapsed" &&
                        (agent.summary ||
                            agent.approach ||
                            agent.certifications?.length > 0) && (
                            <div className="mt-2">
                                <button
                                    type="button"
                                    onClick={() => setExpanded(prev => !prev)}
                                    className="text-sm font-medium text-brand-600 hover:underline">
                                    {expanded ? "Hide details" : "Show details"}
                                </button>
                            </div>
                        )}
                </div>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">{props.children}</div>
            </div>
        </div>
    );
};
